import { useNavigate } from 'react-router-dom';
import { Bookmark, X, FileText, ArrowRight } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { useCourse } from '../context/CourseContext';

export default function BookmarksPanel({ open, onOpenChange, sections }) {
  const navigate = useNavigate();
  const { bookmarks, toggleBookmark } = useCourse();

  const bookmarkDetails = bookmarks.map(bmId => {
    const sec = sections?.find(s => s.subtopics?.some(st => st.id === bmId));
    const st = sec?.subtopics?.find(s => s.id === bmId);
    return sec && st ? { id: bmId, section: sec, subtopic: st } : null;
  }).filter(Boolean);

  const handleNavigate = (sectionId) => {
    onOpenChange(false);
    navigate(`/section/${sectionId}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 gap-0" data-testid="bookmarks-panel">
        <DialogTitle className="sr-only">Bookmarked Topics</DialogTitle>
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Bookmark className="h-4 w-4 text-sf-warning fill-sf-warning" />
            <h3 className="font-heading font-semibold text-sm text-foreground">
              Bookmarked Topics
            </h3>
            <span className="text-xs bg-sf-warning/10 text-sf-warning font-medium px-1.5 py-0.5 rounded-full">
              {bookmarks.length}
            </span>
          </div>
        </div>

        <ScrollArea className="max-h-96">
          {bookmarkDetails.length === 0 ? (
            <div className="py-12 text-center">
              <Bookmark className="h-8 w-8 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No bookmarks yet</p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                Bookmark topics while reading to find them quickly later
              </p>
            </div>
          ) : (
            <div className="p-2 space-y-0.5">
              {bookmarkDetails.map((bm) => (
                <div
                  key={bm.id}
                  className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-secondary transition-colors group"
                  data-testid={`bookmarks-panel-item-${bm.id}`}
                >
                  <button
                    onClick={() => handleNavigate(bm.section.id)}
                    className="flex items-start gap-2.5 flex-1 min-w-0 text-left"
                  >
                    <FileText className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {bm.subtopic.title}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Section {bm.section.id}: {bm.section.title}
                      </p>
                    </div>
                  </button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                    onClick={() => toggleBookmark(bm.id)}
                    data-testid={`remove-bookmark-${bm.id}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
